import {
  type BigDecimal,
  type BigInteger,
  type Char,
  charToCode,
  codeToChar,
  type Schema,
  stringToBigDecimal,
  stringToBigInteger,
} from "@ostack.tech/kform";
import fromExponential from "from-exponential";

// Min/max numeric representations for a `Char`
const CHAR_MIN_NUMBER = 0x0000;
const CHAR_MAX_NUMBER = 0xffff;

/** Minimum Kotlin `Byte` value. */
export const BYTE_MIN_VALUE = -128;
/** Maximum Kotlin `Byte` value. */
export const BYTE_MAX_VALUE = 127;
/** Minimum Kotlin {@link Char} value. */
export const CHAR_MIN_VALUE: Char = codeToChar(CHAR_MIN_NUMBER);
/** Maximum Kotlin {@link Char} value. */
export const CHAR_MAX_VALUE: Char = codeToChar(CHAR_MAX_NUMBER);
/** Minimum Kotlin `Short` value. */
export const SHORT_MIN_VALUE = -32768;
/** Maximum Kotlin `Short` value. */
export const SHORT_MAX_VALUE = 32767;
/** Minimum Kotlin `Int` value. */
export const INT_MIN_VALUE = -2147483648;
/** Maximum Kotlin `Int` value. */
export const INT_MAX_VALUE = 2147483647;
/** Minimum Kotlin `Long` value. */
export const LONG_MIN_VALUE = -9223372036854775808n;
/** Maximum Kotlin `Long` value. */
export const LONG_MAX_VALUE = 9223372036854775807n;

// Representations of the number `0` as `Char`/`BigInteger`/`BigDecimal`
const CHAR_ZERO: Char = CHAR_MIN_VALUE;
const BIG_INTEGER_ZERO = stringToBigInteger("0");
const BIG_DECIMAL_ZERO = stringToBigDecimal("0");

/** Supported numeric types. */
export type Numeric = number | bigint | Char | BigInteger | BigDecimal | string;

/**
 * Formats a numeric {@link value} as a string, given its {@link schema}.
 *
 * @param value Numeric value to format as a string.
 * @param schema Schema of the numeric {@link value}.
 * @returns String representation of the numeric {@link value}.
 */
export function formatNumericAsString<
  T extends Numeric | null = Numeric | null,
>(value: T | undefined, schema: Schema<T>): string {
  if (value == null) {
    return "";
  }
  const schemaType = schema.typeInfo.name;
  switch (schemaType) {
    case "Char":
      return charToCode(value as Char).toString();
    case "Float":
    case "Double":
      // Convert exponential representation of numbers into their
      // non-exponential counterpart, e.g. `1.123e-10` -> `0.0000000001123`
      return Number.isNaN(value as number)
        ? ""
        : fromExponential(value as number);
    case "String":
      return value === "" || Number.isNaN(Number(value as string))
        ? ""
        : fromExponential(value as string);
    default:
      return value.toString();
  }
}

/**
 * Parses a formatted numeric [value]{@link formattedValue} as a numeric value,
 * given the {@link schema} of the expected numeric value.
 *
 * @param formattedValue Formatted value to parse as a numeric value.
 * @param schema Schema of the expected numeric value.
 * @returns Numeric representation of the [formatted value]{@link formattedValue}
 *   according to {@link schema}.
 */
export function parseStringAsNumeric<T extends Numeric | null = Numeric | null>(
  formattedValue: string,
  schema: Schema<T>,
): T {
  const supportsNull = schema.typeInfo.nullable;
  const schemaType = schema.typeInfo.name;
  if (
    formattedValue === "" ||
    formattedValue === "-" ||
    formattedValue === "." ||
    formattedValue === "-."
  ) {
    return (
      supportsNull
        ? null
        : schemaType === "Char"
          ? CHAR_ZERO
          : schemaType === "Long"
            ? 0n
            : schemaType === "String"
              ? ""
              : 0
    ) as T;
  }

  switch (schemaType) {
    case "Byte":
    case "Short":
    case "Int": {
      const { min, max } = numericTypeMinMax(schema as Schema<number>);
      // Int types do not support `NaN`, so we convert it to `null` or `0`
      const value = parseInt(formattedValue);
      return (
        Number.isNaN(value)
          ? supportsNull
            ? null
            : 0
          : Math.min(Math.max(value, min!), max!)
      ) as T;
    }
    case "Char": {
      // Chars do not support `NaN`, so we convert it to `null` or `0`
      const value = parseInt(formattedValue);
      return (
        Number.isNaN(value)
          ? supportsNull
            ? null
            : CHAR_ZERO
          : codeToChar(
              Math.min(Math.max(value, CHAR_MIN_NUMBER), CHAR_MAX_NUMBER),
            )
      ) as T;
    }
    case "Long": {
      // Longs do not support `NaN`, so we return `null` or `0`
      const value = parseIntString(formattedValue);
      if (value === null) {
        return (supportsNull ? null : 0n) as T;
      }
      const bigint = BigInt(value);
      return (
        bigint < LONG_MIN_VALUE
          ? LONG_MIN_VALUE
          : bigint > LONG_MAX_VALUE
            ? LONG_MAX_VALUE
            : bigint
      ) as T;
    }
    case "BigInteger": {
      const value = parseIntString(formattedValue);
      if (value === null) {
        return (supportsNull ? null : BIG_INTEGER_ZERO) as T;
      }
      return stringToBigInteger(value) as T;
    }
    case "BigDecimal": {
      const preferredScale =
        (schema.typeInfo.restrictions.scale as number | undefined) ?? null;
      let bigDecimal: BigDecimal | null;
      try {
        bigDecimal = stringToBigDecimal(formattedValue);
      } catch {
        bigDecimal = supportsNull ? null : BIG_DECIMAL_ZERO;
      }
      return (
        bigDecimal === null ||
        preferredScale === null ||
        bigDecimal.scale >= preferredScale
          ? bigDecimal
          : bigDecimal.setScale(preferredScale)
      ) as T;
    }
    case "Float":
    case "Double":
      // Floats support `NaN`, so no need to check for that
      return parseFloat(formattedValue) as T;
    default:
      return formattedValue as T;
  }
}

/**
 * Returns the minimum and maximum numeric values supported by the given numeric
 * {@link schema}, or `undefined` if there are no such limits.
 *
 * @param schema Numeric schema for which to get mininum/maximum values.
 * @returns Minimum/maximum supported numeric value of {@link schema}.
 */
export function numericTypeMinMax<T extends Numeric | null = Numeric | null>(
  schema: Schema<T>,
): {
  min: NonNullable<T> | undefined;
  max: NonNullable<T> | undefined;
} {
  switch (schema.typeInfo.name) {
    case "Byte":
      return {
        min: BYTE_MIN_VALUE as NonNullable<T>,
        max: BYTE_MAX_VALUE as NonNullable<T>,
      };
    case "Char":
      return {
        min: CHAR_MIN_VALUE as unknown as NonNullable<T>,
        max: CHAR_MAX_VALUE as unknown as NonNullable<T>,
      };
    case "Short":
      return {
        min: SHORT_MIN_VALUE as NonNullable<T>,
        max: SHORT_MAX_VALUE as NonNullable<T>,
      };
    case "Int":
      return {
        min: INT_MIN_VALUE as NonNullable<T>,
        max: INT_MAX_VALUE as NonNullable<T>,
      };
    case "Long":
      return {
        min: LONG_MIN_VALUE as unknown as NonNullable<T>,
        max: LONG_MAX_VALUE as unknown as NonNullable<T>,
      };
    default:
      return { min: undefined, max: undefined };
  }
}

/**
 * Parses a string representing an integer by removing leading zeroes and
 * stopping when a non-digit is found.
 */
function parseIntString(str: string): string | null {
  const len = str.length;
  const isNegative = str.startsWith("-");
  // Ignore leading zeroes
  let start = isNegative ? 1 : 0;
  while (
    start < len - 1 &&
    str[start] === "0" &&
    str[start + 1] >= "0" &&
    str[start + 1] <= "9"
  ) {
    ++start;
  }
  // Ignore non-digits
  let end = len;
  for (let i = start; i < len; ++i) {
    if (str[i] < "0" || str[i] > "9") {
      end = i;
      break;
    }
  }
  if (start === end) {
    return null;
  }
  const normalized = str.slice(start, end);
  return isNegative && normalized !== "0" ? `-${normalized}` : normalized;
}
